import DonateButton from '../components/DonateButton';
import Link from '../components/Link';
import FizzyPortrait from '../components/scene/FizzyPortrait';
import DropperFlaskIcon from '../components/icons/DropperFlaskIcon';
import SafetyGogglesIcon from '../components/icons/SafetyGogglesIcon';
import { ABOUT_CLASSES } from '../constants/classNames';
import {
  ASSISTANT_NAME,
  CONTACT_EMAIL,
  IS_SUPPORT_BUTTON_VISIBLE,
  PUBLISHER_NAME,
  PUBLISHER_URL,
  ROUTES,
  SITE_NAME,
} from '../constants/config';
import '../styles/AboutPage.css';

const ABOUT_CARDS = [
  {
    icon: <DropperFlaskIcon size={64} className={ABOUT_CLASSES.cardArt} />,
    title: 'Learn by playing',
    text: 'Kids pick real elements from the periodic table, mix them and see what happens, with no mess and no danger.',
  },
  {
    icon: <FizzyPortrait size={64} className={ABOUT_CLASSES.cardArt} />,
    title: `Meet ${ASSISTANT_NAME}`,
    text: `${ASSISTANT_NAME} is our kid scientist. After every experiment ${ASSISTANT_NAME} explains why the reaction happened in simple words.`,
  },
  {
    icon: <SafetyGogglesIcon className={ABOUT_CLASSES.cardArtWide} />,
    title: 'Safety first',
    text: 'Every explosive or fiery reaction comes with a safety note, so kids learn that real chemistry needs a grown-up and proper equipment.',
  },
];

// Where we check the facts, the equations and the safety notes.
const REFERENCE_SOURCES = [
  'PubChem, the free chemistry database run by the US National Library of Medicine',
  "The Royal Society of Chemistry's periodic table and element pages",
  'The CRC Handbook of Chemistry and Physics, for melting points, densities and other numbers',
  'Standard university chemistry references, such as Greenwood and Earnshaw\'s Chemistry of the Elements',
];

function StorySection({ title, children }) {
  return (
    <section className={ABOUT_CLASSES.storySection}>
      <h2 className={ABOUT_CLASSES.storyTitle}>{title}</h2>
      {children}
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className={ABOUT_CLASSES.root}>
      <section className={ABOUT_CLASSES.hero}>
        <h1 className={ABOUT_CLASSES.heading}>About Us</h1>
        <p className={ABOUT_CLASSES.lead}>
          {SITE_NAME} is a free virtual chemistry lab for curious kids. We believe the best way to fall in love with
          science is to try things, get surprised, and then find out why.
        </p>
      </section>

      <div className={ABOUT_CLASSES.cards}>
        {ABOUT_CARDS.map(({ icon, title, text }) => (
          <article key={title} className={ABOUT_CLASSES.card}>
            <span className={ABOUT_CLASSES.cardIcon} aria-hidden="true">{icon}</span>
            <h2 className={ABOUT_CLASSES.cardTitle}>{title}</h2>
            <p>{text}</p>
          </article>
        ))}
      </div>

      <div className={ABOUT_CLASSES.story}>
        <StorySection title={`Who makes ${SITE_NAME}`}>
          <p>
            {SITE_NAME} is a project of <a href={PUBLISHER_URL}>{PUBLISHER_NAME}</a>, which makes and runs the site. We
            design the lab, write the explanations and articles, draw {ASSISTANT_NAME} and the lab scenes, and keep
            adding new reactions and pages.
          </p>
        </StorySection>

        <StorySection title="Why we built it">
          <p>
            Real chemistry sets can only do a handful of safe experiments, and many of the most exciting reactions, like
            potassium exploding in water or sodium burning in chlorine, are far too dangerous to try. We wanted a place
            where kids could mix any two of the 118 elements, see what really happens, and hear a friendly explanation of
            why. Every one of the more than 7,000 possible pairs in the lab has an answer, even when that answer is
            "nothing happens".
          </p>
        </StorySection>

        <StorySection title="Who it's for">
          <p>
            {SITE_NAME} is written for children of about 7 to 12 years old, using short sentences and everyday words.
            Parents use it to answer "what happens if...?" questions at home, and teachers use it on classroom screens to
            introduce the periodic table, reactions and lab safety. Older students can use the equations and the
            element pages as a quick reference. Our <Link to={ROUTES.PARENTS}>parents and teachers page</Link> has
            lesson ideas and free printable worksheets.
          </p>
        </StorySection>

        <StorySection title="How we check the chemistry">
          <p>
            We only list a reaction when the two substances really react when they are put together, with heat if
            needed. If we are not sure, the lab says "no direct reaction" rather than guessing. Equations are balanced
            automatically and checked by tests for every pair. Facts, numbers and safety notes are checked against
            trusted references, including:
          </p>
          <ul className={ABOUT_CLASSES.sourceList}>
            {REFERENCE_SOURCES.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
        </StorySection>

        <StorySection title="A note on safety">
          <p>
            Everything in the lab is pretend. Many of the reactions on this site are dangerous in real life, and some
            involve poisonous or radioactive substances. Our "Try this safely at home" ideas only use ordinary kitchen
            materials and should always be done with a grown-up. Real chemicals belong in a proper lab, with goggles and a
            trained adult.
          </p>
        </StorySection>

        <StorySection title="Get in touch">
          <p>
            Spotted a mistake, or have an idea for a new reaction? Grown-ups can email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or visit our <Link to={ROUTES.CONTACT}>contact page</Link>.
            Every correction helps make the lab better for everyone.
          </p>
        </StorySection>
      </div>

      {IS_SUPPORT_BUTTON_VISIBLE && (
        <section className={ABOUT_CLASSES.donateBox}>
          <h2>Help us keep the lab open</h2>
          <p>{SITE_NAME} is free for every kid. Your support goes toward hosting, further development and the team behind it.</p>
          <DonateButton />
        </section>
      )}
    </div>
  );
}
